/**
 * U · Use Case Intelligence catalog entry (ADR-0008 / RFC-0001).
 *
 * U is a curated catalog of real-world use-cases / scenarios mapped to their
 * trigger regulation cluster(s) and key compliance question(s), plus advisory
 * public-case / enforcement signals. It is NOT config→rule traceability
 * (that drifted framing is superseded by ADR-0008).
 *
 * Honesty: only declared-scope rows carry `complete`/`partial` with real
 * `canonicalRuleIds`; everything else is `planned`/`out_of_scope_v0_1` and never
 * claims coverage. Public-case signals are advisory and unverified until a
 * reviewer signs them (`reviewedBy` set). No fabricated regulatory literals.
 */

import { z } from "zod";

/**
 * Coverage-matrix vocabulary (mirrors `docs/governance/COVERAGE-MATRIX.md` +
 * the source-registry). Deliberately distinct from `primitives.CoverageState`
 * (which is per-card evidence state: missing/partial/linked/ready/stale).
 */
export const CoverageMatrixStatus = z.enum([
  "complete",
  "partial",
  "planned",
  "out_of_scope_v0_1",
]);
export type CoverageMatrixStatus = z.infer<typeof CoverageMatrixStatus>;

/**
 * Kind of advisory U·P-band intelligence (RFC-0005 / ADR-0013). Spans VOC voice,
 * enforcement / recall / market-surveillance signals, competitive and IP moves,
 * ratings, industry news, and upstream drafts. None of these are legal sources.
 */
export const IntelType = z.enum([
  "voc_complaint",
  "enforcement_action",
  "recall",
  "market_surveillance",
  "competitor_move",
  "patent",
  "rating",
  "industry_news",
  "draft",
]);
export type IntelType = z.infer<typeof IntelType>;

/**
 * A structured advisory intel signal (RFC-0005 / ADR-0013), upgraded in place
 * from the former free-text `UseCasePublicSignal`. Never a legal source;
 * `reviewedBy` is null until a human verifies it, and the advisory label is a
 * locked literal so the UI cannot present it as canonical fact.
 *
 * Back-compat: `intelType` defaults to `industry_news` (ADR-0013 §50) so a
 * legacy row persisted before this field existed still parses (instead of
 * being silently dropped) and lands on the safe `industry_news` bucket; new
 * code always sets it explicitly. The other ADR-0013 additions (`intelType`,
 * `topicId`, `occurredAt`, `entity`, `jurisdiction`, `status`) are
 * optional/nullable/defaulted so rows carrying only the original fields still
 * parse. `useCaseId` is NOT an ADR-0013 field — it predates it (RFC-0003 D4 /
 * 0.1.5) and remains optional.
 */
export const IntelSignalSchema = z.object({
  intelType: IntelType.default("industry_news"),
  summary: z.string().min(1),
  sourceUrl: z.string().url(),
  rawSha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/, "rawSha256 must be lowercase hex (normalised form)")
    .optional(),
  advisoryLabel: z.literal("use_case_signal_not_legal_source"),
  reviewedBy: z.string().min(1).nullable(),
  // ADR-0013 structured fields — all optional/nullable for back-compat.
  topicId: z.string().min(1).optional(),
  occurredAt: z.string().optional(),
  entity: z.string().min(1).nullable().optional(),
  jurisdiction: z.string().min(1).nullable().optional(),
  status: z.string().min(1).nullable().optional(),
  // Optional link to a catalog use-case (RFC-0003 D4 / 0.1.5, predates ADR-0013).
  // Omitted = a standalone enforcement-case entry shown in "Case intelligence".
  useCaseId: z.string().min(1).optional(),
});
export type IntelSignal = z.infer<typeof IntelSignalSchema>;

// Back-compat aliases (RFC-0005 ADR-0013): the U-band signal was upgraded in
// place from `UseCasePublicSignal`; keep the old names exported so existing
// imports (incl. `UseCaseEntrySchema.publicCaseSignal`) keep compiling.
export const UseCasePublicSignalSchema = IntelSignalSchema;
export type UseCasePublicSignal = IntelSignal;

export const UseCaseEntrySchema = z
  .object({
    id: z.string().min(1),
    useCase: z.string().min(1),
    regulationClusters: z.array(z.string().min(1)).min(1),
    keyIssues: z.array(z.string().min(1)).min(1),
    coverageStatus: CoverageMatrixStatus,
    // `.default(() => [])` (factory) so each parse gets a fresh array — a bare
    // `.default([])` shares one mutable reference across all parses.
    canonicalRuleIds: z.array(z.string().min(1)).default(() => []),
    lifecycleState: z.enum(["ACTIVE", "SEED_UNVERIFIED", "PLACEHOLDER"]),
    publicCaseSignal: UseCasePublicSignalSchema.optional(),
  })
  .superRefine((entry, ctx) => {
    // Honesty invariant (ADR-0008), machine-enforced at every parse: only
    // declared-scope rows (complete/partial) may claim canonical-rule backing;
    // planned/out_of_scope_v0_1 rows never do.
    const declared =
      entry.coverageStatus === "complete" || entry.coverageStatus === "partial";
    if (declared && entry.canonicalRuleIds.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["canonicalRuleIds"],
        message: "complete/partial entries must reference at least one canonical rule id",
      });
    }
    if (!declared && entry.canonicalRuleIds.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["canonicalRuleIds"],
        message: "planned/out_of_scope_v0_1 entries must not claim canonical coverage",
      });
    }
  });
export type UseCaseEntry = z.infer<typeof UseCaseEntrySchema>;
